import { LockOutlineRounded } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import Typography from "@mui/material/Typography";
import CountryDropdown from "@/components/giftExperience/CountryDropdown";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";

type Props = {
  selectedCountry: string;
  onSelectCountry: (country: string) => void;
  /** campaign_send.regions — may the client ship outside the US. */
  canShipInternationally: boolean;
  /** collection.edit — may the client change the curated grid. */
  canEditCollection: boolean;
  onEnterEdit: () => void;
};

export default function CollectionViewControls({
  selectedCountry,
  onSelectCountry,
  canShipInternationally,
  canEditCollection,
  onEnterEdit,
}: Props) {
  const { openModal, openPlanModal } = useCampaignBuilder();

  return (
    <div className="flex items-center justify-between px-5 py-3">
      {/* Left: "Available Gifts in" + country selector */}
      <div className="flex items-center gap-4">
        <Typography
          sx={{
            fontFamily: "var(--font-lato)",
            fontSize: "16px",
            color: "var(--color-rodeo-black)",
            whiteSpace: "nowrap",
          }}
        >
          Available Gifts in
        </Typography>

        <CountryDropdown
          selectedCountry={selectedCountry}
          onChange={(next) => {
            if (!canShipInternationally && next !== "US") {
              // The selection IS applied, so the dropdown shows the country the user picked
              // while the upsell is up. Every way out other than Upgrade rolls it back to the
              // country they came from: the same function backs both "Maybe Later" and
              // dismissal (backdrop or Escape), so the dialog cannot be brushed past into a
              // state the plan does not allow.
              const previous = selectedCountry;
              const decline = () => onSelectCountry(previous);
              onSelectCountry(next);
              openModal({
                context: "international",
                onPrimary: () => openPlanModal("international"),
                onSecondary: decline,
                onDismiss: decline,
              });
              return;
            }
            onSelectCountry(next);
          }}
        />
      </div>

      {/* Right: Edit Gifts button */}
      <button
        type="button"
        onClick={() => {
          if (!canEditCollection) {
            openModal({
              context: "edit",
              onPrimary: () => openPlanModal("edit"),
            });
            return;
          }
          onEnterEdit();
        }}
        className="h-8.5 px-4 rounded-btn font-lato font-bold text-[11px] tracking-[2px] uppercase border border-dark-dust text-rodeo-black flex items-center gap-2 transition-colors hover:bg-light-dust cursor-pointer"
      >
        <EditIcon sx={{ fontSize: 13 }} />
        Edit Gifts
        {!canEditCollection && <LockOutlineRounded sx={{ fontSize: 13 }} />}
      </button>
    </div>
  );
}
